import type { ProductDemoFeature, ProductDemoFeatureId } from './productDemoFeatures';
import { PRODUCT_DEMO_FEATURES } from './productDemoFeatures';
import type { SearchTourPrepareAction } from './searchTourSteps';

export const DEMO_TOUR_PARAM = 'demoTour';
export const DEMO_FEATURE_PARAM = 'demoFeature';
export const DEMO_STEP_PARAM = 'demoStep';

export type SpotlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type TipPlacement = {
  top: number;
  left: number;
  maxWidth: number;
};

const getDefaultSearchUrl = () => {
  return '/examples?view=DISEASES&idents=C0028754&source=ALL&tab=GDA';
};

export function buildTourUrl(feature: ProductDemoFeature, stepIndex?: number): string {
  const isSearch = feature.id === 'search';
  const baseUrl = isSearch ? getDefaultSearchUrl() : feature.toolHref;

  const [pathAndHash, queryString = ''] = baseUrl.split('?');
  const [path, hashFragment = ''] = pathAndHash.split('#');
  const hash = hashFragment ? `#${hashFragment}` : '';
  const params = new URLSearchParams(queryString);
  params.set(DEMO_TOUR_PARAM, '1');
  params.set(DEMO_FEATURE_PARAM, feature.id);

  if (stepIndex != null) params.set(DEMO_STEP_PARAM, String(stepIndex + 1));
  else params.delete(DEMO_STEP_PARAM);

  return `${path}?${params.toString()}${hash}`;
}

export function getTourFeatureId(params: URLSearchParams): ProductDemoFeatureId | null {
  const raw = params.get(DEMO_FEATURE_PARAM);
  if (!raw) return null;
  return PRODUCT_DEMO_FEATURES.some(f => f.id === raw) ? (raw as ProductDemoFeatureId) : null;
}

export function queryTourEl(selector: string): HTMLElement | null {
  return queryVisible(selector) ?? (document.querySelector(selector) as HTMLElement | null);
}

export function queryVisible(selector: string): HTMLElement | null {
  const nodes = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
  return (
    nodes.find(el => {
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.visibility !== 'hidden' &&
        style.display !== 'none'
      );
    }) ??
    nodes[0] ??
    null
  );
}

function clickIfPresent(selector: string) {
  const el = queryVisible(selector);
  if (el && typeof el.click === 'function') {
    el.click();
  }
}

function isTableControlsOpen() {
  const panel = document.querySelector('[data-demo-tour="table-controls"]') as HTMLElement | null;
  // The aside stays mounted while collapsed — only trust the open flag / open-only content.
  if (panel?.getAttribute('data-demo-open') === 'true') return true;
  return Boolean(
    queryVisible(
      '[data-demo-tour="sidebar-columns"], [data-demo-tour="sidebar-search-items"], [data-demo-tour="sidebar-my-score"]',
    ),
  );
}

function ensureSidebarOpen() {
  if (isTableControlsOpen()) return;

  // Desktop collapsed rail: explicit open control (never toggle-close).
  if (queryVisible('[data-demo-tour="open-table-controls"]')) {
    clickIfPresent('[data-demo-tour="open-table-controls"]');
    return;
  }

  // Toolbar / mobile fallback.
  if (!isTableControlsOpen()) {
    clickIfPresent('[data-demo-tour="sidebar-toggle"]');
  }
}

function ensureSectionExpanded(sectionSelector: string) {
  ensureSidebarOpen();

  const section =
    queryVisible(sectionSelector) ??
    (document.querySelector(sectionSelector) as HTMLElement | null);
  if (!section) return;

  const tourKey = section.getAttribute('data-demo-tour');
  const toggle =
    (tourKey
      ? section.querySelector<HTMLElement>(`[data-demo-tour="${tourKey}-toggle"]`)
      : null) ?? section.querySelector<HTMLElement>('[aria-expanded]');
  if (toggle && toggle.getAttribute('aria-expanded') === 'false') {
    toggle.click();
  }

  section.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
}

function collapseSection(sectionSelector: string) {
  const section = document.querySelector(sectionSelector) as HTMLElement | null;
  if (!section) return;

  const tourKey = section.getAttribute('data-demo-tour');
  const toggle =
    (tourKey
      ? section.querySelector<HTMLElement>(`[data-demo-tour="${tourKey}-toggle"]`)
      : null) ?? section.querySelector<HTMLElement>('[aria-expanded]');
  if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
    toggle.click();
  }
}

/** Open My Score panel with enough room and seed a source so weights UI is visible. */
function prepareMyScorePanel() {
  ensureSidebarOpen();
  // Columns / Search items from previous steps steal vertical space.
  collapseSection('[data-demo-tour="sidebar-columns"]');
  collapseSection('[data-demo-tour="sidebar-search-items"]');

  const toggle =
    queryVisible('[data-demo-tour="sidebar-my-score-toggle"]') ??
    (document.querySelector('[data-demo-tour="sidebar-my-score-toggle"]') as HTMLElement | null);

  if (toggle) {
    if (toggle.getAttribute('aria-expanded') === 'false') {
      toggle.click();
    }
    toggle.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  } else {
    ensureSectionExpanded('[data-demo-tour="sidebar-my-score"]');
  }

  const content =
    queryVisible('[data-demo-tour="sidebar-my-score-content"]') ??
    (document.querySelector('[data-demo-tour="sidebar-my-score-content"]') as HTMLElement | null);
  if (!content) return;

  content.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });

  // Seed at least one source so threshold + weight sliders appear.
  const hasWeights = Boolean(content.querySelector('.my-score-slider'));
  if (!hasWeights) {
    if (!queryVisible('[data-demo-tour="my-score-source-picker"]')) {
      clickIfPresent('[data-demo-tour="my-score-select-sources"]');
    }
    clickIfPresent('[data-demo-tour="my-score-source-option"]');
  } else if (queryVisible('[data-demo-tour="my-score-source-picker"]')) {
    // Close picker so the weights / threshold UI is the clear focus.
    clickIfPresent('[data-demo-tour="my-score-select-sources"]');
  }
}

export function prepareSearchStep(actions: SearchTourPrepareAction[] | undefined) {
  if (!actions?.length) return;

  for (const action of actions) {
    switch (action) {
      case 'open-actions': {
        if (
          !queryVisible(
            '[data-demo-tour="actions-download"], [data-demo-tour="actions-enrichment"], [data-demo-tour="actions-save-search"]',
          )
        ) {
          clickIfPresent('[data-demo-tour="actions-button"]');
        }
        break;
      }
      case 'close-actions': {
        if (
          queryVisible(
            '[data-demo-tour="actions-download"], [data-demo-tour="actions-enrichment"], [data-demo-tour="actions-save-search"]',
          )
        ) {
          clickIfPresent('[data-demo-tour="actions-button"]');
        }
        break;
      }
      case 'open-column-menu': {
        if (!queryVisible('[data-demo-tour="column-pin"], [data-demo-tour="column-hide"]')) {
          clickIfPresent('[data-demo-tour="column-options"]');
        }
        break;
      }
      case 'close-column-menu': {
        if (queryVisible('[data-demo-tour="column-pin"], [data-demo-tour="column-hide"]')) {
          clickIfPresent('[data-demo-tour="column-options"]');
        }
        break;
      }
      case 'open-row-detail': {
        if (!queryVisible('[data-demo-tour="historical-score-tab"]')) {
          clickIfPresent('[data-demo-tour="open-row-detail"]');
        }
        break;
      }
      case 'aida-open-rename': {
        clickIfPresent('[data-demo-tour="aida-edit-chat-name"]');
        break;
      }
      case 'advanced-search-run-search-list': {
        if (
          !queryVisible(
            '[data-demo-tour="advanced-search-row-checkbox"], [data-demo-tour="advanced-search-matches-table"]',
          )
        ) {
          clickIfPresent('[data-demo-tour="advanced-search-search-list"], #advanced_search_search_list');
        }
        break;
      }
      case 'advanced-search-show-examples': {
        if (queryVisible('[data-demo-tour="advanced-search-show-examples"], #advanced_search_show_examples')) {
          clickIfPresent('[data-demo-tour="advanced-search-show-examples"], #advanced_search_show_examples');
        }
        break;
      }
      case 'advanced-search-back-to-input': {
        // Results view hides the textarea — Back restores the first-steps UI.
        if (
          !queryVisible('[data-demo-tour="advanced-search-input-textarea"]') &&
          queryVisible('[data-demo-tour="advanced-search-back"], #advanced_search_back')
        ) {
          clickIfPresent('[data-demo-tour="advanced-search-back"], #advanced_search_back');
        }
        break;
      }
      case 'enrichment-show-examples': {
        clickIfPresent('[data-demo-tour="enrichment-show-examples"]');
        break;
      }
      case 'enrichment-open-modal': {
        clickIfPresent('[data-demo-tour="enrichment-run-button"]');
        break;
      }
      case 'enrichment-run-enrichment': {
        clickIfPresent('[data-demo-tour="enrichment-run-button"]');
        clickIfPresent('[data-demo-tour="enrichment-modal-submit"]');
        break;
      }
      case 'enrichment-submit-modal': {
        clickIfPresent('[data-demo-tour="enrichment-modal-submit"]');
        break;
      }
      case 'enrichment-back-to-input': {
        if (queryVisible('[data-demo-tour="enrichment-back-to-setup"], #enrichment_back_to_setup')) {
          clickIfPresent('[data-demo-tour="enrichment-back-to-setup"], #enrichment_back_to_setup');
        }
        break;
      }
      case 'enrichment-close-modal': {
        if (queryVisible('[data-demo-tour="enrichment-modal-root"]')) {
          clickIfPresent('[data-demo-tour="enrichment-modal-close"]');
          clickIfPresent('#enrichment_cancel');
        }
        break;
      }
      case 'api-authorize': {
        clickIfPresent('.api-console-authorize-btn');
        break;
      }
      case 'api-load-examples': {
        clickIfPresent('.api-console-fill-examples');
        break;
      }
      case 'api-try-it-out': {
        clickIfPresent('.api-console-send');
        break;
      }
      case 'open-table-controls':
        ensureSidebarOpen();
        break;
      case 'expand-sidebar-columns':
        ensureSectionExpanded('[data-demo-tour="sidebar-columns"]');
        break;
      case 'expand-sidebar-my-score':
        prepareMyScorePanel();
        break;
      case 'expand-sidebar-search-items':
        ensureSectionExpanded('[data-demo-tour="sidebar-search-items"]');
        break;
      case 'sticky-open-palette': {
        if (!queryVisible('[data-demo-tour="sticky-notes-palette"]')) {
          clickIfPresent('[data-demo-tour="sticky-notes-toggle"], button[aria-label="Sticky notes"]');
        }
        break;
      }
      case 'sticky-create-note':
      case 'sticky-ensure-open-note': {
        if (queryVisible('[data-demo-tour="sticky-note"]')) break;
        if (!queryVisible('[data-demo-tour="sticky-notes-palette"]')) {
          clickIfPresent('[data-demo-tour="sticky-notes-toggle"], button[aria-label="Sticky notes"]');
        }
        clickIfPresent(
          '[data-demo-tour="sticky-notes-swatch"][data-demo-color="yellow"], [aria-label="Add Yellow sticky note"]',
        );
        break;
      }
      case 'sticky-minimize-note': {
        clickIfPresent('[data-demo-tour="sticky-note-minimize"]');
        break;
      }
      case 'sticky-prepare-restore': {
        if (!queryVisible('[data-demo-tour="sticky-notes-restore"]')) {
          if (queryVisible('[data-demo-tour="sticky-note-minimize"]')) {
            clickIfPresent('[data-demo-tour="sticky-note-minimize"]');
          } else if (!queryVisible('[data-demo-tour="sticky-note"]')) {
            if (!queryVisible('[data-demo-tour="sticky-notes-palette"]')) {
              clickIfPresent('[data-demo-tour="sticky-notes-toggle"], button[aria-label="Sticky notes"]');
            }
            clickIfPresent(
              '[data-demo-tour="sticky-notes-swatch"][data-demo-color="yellow"], [aria-label="Add Yellow sticky note"]',
            );
          }
        }
        if (!queryVisible('[data-demo-tour="sticky-notes-palette"]')) {
          clickIfPresent('[data-demo-tour="sticky-notes-toggle"], button[aria-label="Sticky notes"]');
        }
        break;
      }
      default:
        break;
    }
  }
}

export function computeCornerTipPlacement(tipHeightEstimate = 200): TipPlacement {
  const tipWidth = Math.min(380, window.innerWidth - 24);
  const pad = 16;
  return {
    top: Math.max(pad, window.innerHeight - tipHeightEstimate - pad),
    left: Math.max(pad, window.innerWidth - tipWidth - pad),
    maxWidth: tipWidth,
  };
}

export function computeTipPlacement(
  target: SpotlightRect | null,
  tipHeightEstimate = 200,
): TipPlacement {
  const tipWidth = Math.min(380, window.innerWidth - 24);
  const gap = 12;
  const pad = 12;
  const maxTop = Math.max(pad, window.innerHeight - tipHeightEstimate - pad);

  if (!target) {
    return computeCornerTipPlacement(tipHeightEstimate);
  }

  const spaceRight = window.innerWidth - (target.left + target.width) - pad;
  const spaceLeft = target.left - pad;
  const spaceBelow = window.innerHeight - (target.top + target.height) - pad;
  const spaceAbove = target.top - pad;
  const clampTop = (top: number) => Math.min(Math.max(pad, top), maxTop);
  const clampLeft = (left: number) =>
    Math.min(Math.max(pad, left), window.innerWidth - tipWidth - pad);

  // Prefer beside the target (never overlapping the spotlight hole).
  if (spaceRight >= tipWidth + gap) {
    return {
      top: clampTop(target.top),
      left: target.left + target.width + gap,
      maxWidth: tipWidth,
    };
  }

  if (spaceLeft >= tipWidth + gap) {
    return {
      top: clampTop(target.top),
      left: Math.max(pad, target.left - tipWidth - gap),
      maxWidth: tipWidth,
    };
  }

  // Full-width / tall targets (e.g. Advanced Search textarea): place outside below/above.
  if (spaceBelow >= Math.min(tipHeightEstimate, 160) + gap) {
    return {
      top: clampTop(target.top + target.height + gap),
      left: clampLeft(target.left),
      maxWidth: tipWidth,
    };
  }

  if (spaceAbove >= Math.min(tipHeightEstimate, 160) + gap) {
    return {
      top: clampTop(target.top - tipHeightEstimate - gap),
      left: clampLeft(target.left),
      maxWidth: tipWidth,
    };
  }

  // Last resort: bottom-right corner (outside the hole).
  return computeCornerTipPlacement(tipHeightEstimate);
}
